// MissionDisplay component: Renders the list of missions, pagination, and the mission detail view.
import { LinkIcon } from 'assets/icons';
import AssignMenu from 'components/assign/AssignMenu';
import Button from 'components/button';
import MissionCard from 'components/Mission/MissionCard';
import CopyToClipboard from 'components/copyToClipboard';
import EmptyState from 'components/EmptyState/EmptyState';
import Pagination from 'components/pagination/pagination';
import Tooltip from 'components/tooltips/Tooltips';
import MediaQuery from 'react-responsive';
import { useEffect } from 'react';
import useSlideOver from 'components/SlideOver/useSlideOver';
import UseContext from 'hooks/useContext';
import { countUnreadApplications, resolveMissionSeenAt } from 'helper/unreadApplications';
import AdminMissionDetails from 'pages/pages/mission/adminMissionDetails';
import ApiDatabase from 'server';
import { buildMatchPresentation } from 'utils/searchResultHighlight';
import PopOnChange from 'components/badge/PopOnChange';

const MissionDisplay = ({
  searchMissionResult,
  search = '',
  editMission,
  setEditMission,
  selectedMissions,
  employees,
  openCompanyInfo,
  openTempWorkerInfo,
  handleSelectMission,
  updateEmployeeForMission,
  currentPage,
  countPerPage,
  countTotal,
  totalPages,
  setCurrentPage,
  setSearchMissionResult,
}) => {
  const { toggleSlideOver } = useSlideOver();
  const {
    setObjID,
    language,
    missionApplicationsViews,
    mergeMissionApplicationsViews,
  } = UseContext();
  const visibleMissionIds = (searchMissionResult || [])
    .map((mission) => mission.id_mission_real?.toString?.())
    .filter(Boolean);
  const visibleMissionIdsKey = visibleMissionIds.join(',');

  // Last visits of the current admin, for the unread badge of each mission.
  useEffect(() => {
    if (visibleMissionIds.length === 0) return;
    ApiDatabase.getMissionApplicationsViews(
      { idMissions: visibleMissionIds },
      (views) => mergeMissionApplicationsViews(views),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, [visibleMissionIdsKey]);

  const handleMissionClick = (missionId) => {
    const token = localStorage.getItem('xsrfToken');
    ApiDatabase.postLogMissionClick(
      { token, idMission: missionId },
      () => {},
      (err) => console.error('Log mission click error:', err)
    );

    if (window.innerWidth < 1200) {
      setObjID(missionId);
      toggleSlideOver('get_mission');
    } else {
      setEditMission(missionId);
    }
  };

  return (
    <>
      {/* Left side: Mission list and pagination */}
      <div className="flex-1 h-full w-full lg:w-[555px] mission-container-body-list flex flex-col">
        <div className="flex flex-col gap-3 overflow-y-auto flex-1">
            {[...searchMissionResult].sort((a, b) => {
              const inactiveStatuses = ['suspended', 'canceled', 'interrupted', 'deleted'];
              const aUrgent = a.is_urgent && !inactiveStatuses.includes(a.status) ? 1 : 0;
              const bUrgent = b.is_urgent && !inactiveStatuses.includes(b.status) ? 1 : 0;
              return bUrgent - aUrgent;
            }).map((mission, index) => {
              const missionKey =
                mission.id_mission_real?.toString?.() || mission._id;
              const match = buildMatchPresentation({
                search,
                item: mission,
                context: 'missionList',
              });

              return (
              <div
                key={missionKey}
                className="motion-safe:animate-fadeUp"
                // Capped so the bottom of a long page does not wait for the top.
                style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}
              >
              <MissionCard
                mission={mission}
                match={match}
                selected={
                  (editMission?.toString?.() || editMission) ===
                  (mission.id_mission_real?.toString?.() || mission.id_mission_real)
                }
                onClick={() => handleMissionClick(mission.id_mission_real)}
                // What an action changes in place (assignment, status, edit from
                // the detail panel); the applications count pops on its own.
                // The period is left out: its edit mutates this list in place,
                // so the flash would fire on some later, unrelated render.
                flashSignature={[
                  mission.status,
                  mission.admin_employee_assigned ?? null,
                  mission.user?.user_id ?? null,
                  mission.title,
                  mission.reference ?? null,
                  mission.contract_tempworker?.[0]?.id_bestt ?? null,
                  Boolean(mission.is_urgent),
                ]}
                selection={{
                  checked: selectedMissions.includes(mission.id_mission_real),
                  onChange: (checked) =>
                    handleSelectMission(mission.id_mission_real, checked),
                }}
                tempWorker={mission.user}
                onOpenCompany={() => openCompanyInfo(mission.company?.id_company)}
                onOpenTempWorker={() => openTempWorkerInfo(mission.user?.user_id)}
                actions={
                  <>
                    <div className="hidden group-hover:flex gap-1.5 items-center">
                      {/* Button to copy the mission URL to clipboard */}
                      <CopyToClipboard
                        text={`${process.env.REACT_APP_INTERIM}ficheMissionSearch/${mission.id_mission_real}`}
                        tooltipText="Copier le lien"
                      >
                        <Button size="XS" color="DEFAULT" className="w-[24px] h-[24px] rounded border border-transparent hover:shadow-none hover:border-gray-100 p-0 flex items-center justify-center">
                          <LinkIcon wh={16} color="#374151" />
                        </Button>
                      </CopyToClipboard>
                    </div>
                    {(() => {
                      // Legacy applications have no `refused` field: only an
                      // explicit refusal leaves the count.
                      const applicationsCount = (mission.users || []).filter(
                        (user) => user.refused !== true
                      ).length;
                      const unreadCount = mission.user
                        ? 0
                        : countUnreadApplications(
                            mission.users,
                            resolveMissionSeenAt(
                              missionApplicationsViews,
                              mission.id_mission_real
                            )
                          );
                      return (
                        <div className="relative">
                          <div className="border rounded min-w-[24px] px-1.5 h-[24px] flex items-center justify-center text-xs">
                            <PopOnChange value={applicationsCount} />
                          </div>
                          {unreadCount > 0 && (
                            <Tooltip
                              placement="top"
                              text={
                                unreadCount > 1
                                  ? language
                                      .getValue('unreadApplications.badgeOther', '')
                                      .replace('{count}', unreadCount)
                                  : language.getValue('unreadApplications.badgeOne', '')
                              }
                            >
                              <div className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                            </Tooltip>
                          )}
                        </div>
                      );
                    })()}
                    {/* Employee assignment: the shared menu of the CRM (components/assign). */}
                    <span
                      className="flex"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    >
                      <AssignMenu
                        value={mission.admin_employee_assigned || null}
                        employees={employees.map((employee) => ({
                          id: employee._id,
                          firstname: employee.firstname,
                          lastname: employee.lastname,
                        }))}
                        allowUnassigned
                        onChange={(employeeId) =>
                          updateEmployeeForMission(mission.id_mission_real, [
                            { value: 'unassign', checked: !employeeId },
                            ...employees.map((employee) => ({
                              value: employee._id,
                              checked: employee._id === employeeId,
                            })),
                          ])
                        }
                      />
                    </span>
                  </>
                }
              />
              </div>
            );
            })}
          </div>
          {/* Pagination component for navigating through mission pages */}
        {currentPage && setCurrentPage && (
          <div className="flex-shrink-0 border-t border-gray-100 bg-white px-8 py-4">
          <Pagination
            currentPage={currentPage}
            countPerPage={countPerPage}
            countTotal={countTotal}
              totalPages={totalPages || 1}
            setCurrentPage={setCurrentPage}
          />
        </div>
        )}
      </div>
      {/* Right side: Detailed view of the selected mission - Hidden under 1200px */}
      <MediaQuery minWidth={1200}>
      <div className="flex-1 overflow-hidden mission-container-body-detail">
        {editMission ? (
          <AdminMissionDetails
            missionId={editMission}
            searchMissionResult={searchMissionResult}
            setSearchMissionResult={setSearchMissionResult}
          />
        ) : (
          <EmptyState type="select_mission" />
        )}
      </div>
      </MediaQuery>
    </>
  );
};

export default MissionDisplay;
